import { useState } from "react";
import { Box, Button, InputAdornment, Paper, Stack, TextField, Typography } from "@mui/material";
import { Info } from "@/components/VulnerabilityWatch/Info.tsx";
import { ChoicePanel, FirstSeenPanel, FlagPanel, PresetsPanel } from "@/components/VulnerabilityWatch/Sources/SourcesPanels.tsx";
import type { FurthestStep, PortalOverview, ShieldAnswer, SourcesFilters, WhoBucket } from "@/types";
import { EMPTY_SOURCES_FILTERS, MAX_SEARCH_LENGTH } from "@/utils/sourcesFilters.ts";
import { ANSWER_OPTIONS, countryOptions, groupOptions, networkOptions, STEP_OPTIONS, WHO_OPTIONS } from "@/utils/sourcesSidebar.ts";
import * as copy from "@/utils/vulnerabilityCopy.ts";

function SourcesSearch({ value, onSubmit }: { readonly value: string; readonly onSubmit: (search: string) => void }) {
  const [draft, setDraft] = useState(value);
  return (
    <form role="search" onSubmit={(event) => { event.preventDefault(); onSubmit(draft.trim()); }}>
      <Box sx={{ mb: 1 }}>
        <TextField
          size="small"
          fullWidth
          label={copy.SEARCH_LABEL}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          slotProps={{
            htmlInput: { maxLength: MAX_SEARCH_LENGTH },
            input: { endAdornment: <InputAdornment position="end"><Info text={copy.TIP_SEARCH} /></InputAdornment> },
          }}
        />
      </Box>
    </form>
  );
}

export default function SourcesSidebar({ filters, onChange, overview, windowDays, yourProductKnown }: {
  readonly filters: SourcesFilters;
  readonly onChange: (filters: SourcesFilters) => void;
  readonly overview: PortalOverview | undefined;
  readonly windowDays: number | null;
  readonly yourProductKnown: boolean;
}) {
  const set = (patch: Partial<SourcesFilters>) => onChange({ ...filters, ...patch });
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle2">{copy.FILTERS}</Typography>
        <Button size="small" onClick={() => onChange({ ...EMPTY_SOURCES_FILTERS, sort: filters.sort, order: filters.order })}>
          {copy.RESET}
        </Button>
      </Stack>
      <SourcesSearch key={filters.search} value={filters.search} onSubmit={(search) => set({ search })} />
      <PresetsPanel filters={filters} onChange={onChange} yourProductKnown={yourProductKnown} />
      <ChoicePanel title={copy.FILTER_LOOKED_FOR} options={groupOptions(overview)} selected={filters.groups}
        onChange={(groups) => set({ groups })} />
      <FlagPanel title={copy.FILTER_YOUR_PRODUCT} label={copy.PRESET_YOUR_PRODUCT} checked={filters.yourProduct}
        disabled={!yourProductKnown} note={yourProductKnown ? undefined : copy.NOT_KNOWN}
        onChange={(yourProduct) => set({ yourProduct })} />
      <ChoicePanel title={copy.FILTER_STEP} options={STEP_OPTIONS} selected={filters.furthestSteps}
        onChange={(values) => set({ furthestSteps: values as FurthestStep[] })} />
      <ChoicePanel title={copy.FILTER_ANSWER} options={ANSWER_OPTIONS} selected={filters.answers}
        onChange={(values) => set({ answers: values as ShieldAnswer[] })} />
      <ChoicePanel title={copy.FILTER_WHO} options={WHO_OPTIONS} selected={filters.who}
        onChange={(values) => set({ who: values as WhoBucket[] })} />
      <ChoicePanel title={copy.FILTER_COUNTRY} options={countryOptions(overview)} selected={filters.countries}
        onChange={(countries) => set({ countries })} />
      <ChoicePanel title={copy.FILTER_NETWORK} options={networkOptions(overview)} selected={filters.networks}
        onChange={(networks) => set({ networks })} />
      <FirstSeenPanel days={filters.firstSeenDays} windowDays={windowDays} onChange={(firstSeenDays) => set({ firstSeenDays })} />
      <FlagPanel title={copy.NAMED_LATER} label={copy.NAMED_LATER} checked={filters.namedLater}
        onChange={(namedLater) => set({ namedLater })} />
    </Paper>
  );
}
